import React, { useCallback, useEffect, useRef } from 'react';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { fromJS } from 'immutable';
import { IntlProvider, FormattedMessage } from 'react-intl';
import {
  actions,
  MainLayout,
  getUrlQueryParams,
  getUrlStringFromQueryParams,
  getBaseDlsResourcePath,
  isBreakpointUp,
  ROOT_CONNECT_STATE_PATH,
  UI_FLOWS,
  DEFAULT_LOCALE,
  SPLUNK_EVENT_STATUS, STATUS_CODES,
} from '@americanexpress/amex-connect-core';
import { initiatePartner as initiateConnectPartner } from '../partner';
import { initiateInstantMembership as startInstantMembership } from '../instantMembership';
import { fetchUserLookup } from '../fetchUserLookup';
import { updateUserSession } from '../userSession';
import { GetErrorMessage } from './GetErrorMessage';
import { getMainLayoutFields } from '../getMainLayoutFields';
import styles from './pageWrapper.scss';

const DLS_VERSION = '2.16.0';
const MODULE_NAME = 'amex-connect-page-wrapper';

export const AmexConnectPageWrapper = ({
  children,
  router,
  params,
  location,
  locale,
  languageData,
}) => {
  const dispatch = useDispatch();
  const { mergeValues } = actions;
  const connectState = useSelector((state) => {
    const connectStatePath = state.getIn(ROOT_CONNECT_STATE_PATH);
    const partner = connectStatePath.get('partner');
    const partnerConfig = state.getIn(['config', 'partnerConfig', partner]) || fromJS({});
    return {
      correlationId: connectStatePath.get('correlationId'),
      tenantUrl: state.getIn(['config', 'tenantUrl']),
      amexUrl: state.getIn(['config', 'amexUrl']),
      functionsUrl: state.getIn(['config', 'functionsUrl']),
      partner,
      partnerConfig: partnerConfig.toJS(),
      isAccountsLoaded: connectStatePath.get('isAccountsLoaded'),
      amexConnectBaseResourcePath: state.getIn(['config', 'gemUrl']),
      connectApiUrl: state.getIn(['config', 'connectApiUrl']),
      isInstant: connectStatePath.get('isInstant'),
      hasUserCancelled: connectStatePath.get('hasUserCancelled'),
      currentFlow: connectStatePath.get('currentFlow'),
      isLoading: connectStatePath.get('isLoading'),
      isLoadedFromStorage: connectStatePath.get('isLoadedFromStorage'),
      source: connectStatePath.get('source'),
      isMissingFields: connectStatePath.get('isMissingFields'),
      partnerFailureUri: connectStatePath.get('partnerFailureUri'),
      isAuthenticated: connectStatePath.get('isAuthenticated'),
      provisionId: connectStatePath.get('provisionId'),
      error: connectStatePath.get('error'),
    };
  });

  const {
    correlationId,
    partner,
    tenantUrl,
    amexUrl,
    functionsUrl,
    partnerConfig,
    isAccountsLoaded,
    amexConnectBaseResourcePath,
    connectApiUrl,
    isInstant,
    hasUserCancelled,
    currentFlow,
    isLoading,
    isLoadedFromStorage,
    source,
    isMissingFields,
    partnerFailureUri,
    isAuthenticated,
    provisionId,
    error,
  } = connectState;

  const {
    code,
    urlId,
    correlationId: urlCorrelationId,
    provisionId: urlProvisionId,
    locale: urlLocale,
    partnerFailureUri: urlPartnerFailureUri,
    mockCards,
    mockFlow,
    mockError,
    flow,
    step,
    loginRequired,
  } = getUrlQueryParams(location.search);

  const prevIsInstantRef = useRef();
  const prevCurrentFlowRef = useRef();
  const prevIsLoadedFromStorageRef = useRef();
  const prevProvisionIdRef = useRef();
  const prevCorrelationIdRef = useRef();
  const prevLocaleRef = useRef();
  const prevHasUserCancelledRef = useRef();

  useEffect(() => {
    const splunkLogQueue = [];
    if (step === 'userLookUp') {
      const redirectToLogin = () => {
        const dest = `${encodeURIComponent(`${tenantUrl}/bpay?step=userLookUp&loginRequired=true`)}`;
        window.location.href = `${amexUrl}/en-au/account/login?DestPage=${dest}`;
      };
      if (!loginRequired) {
        updateUserSession(functionsUrl).then((response) => {
          if (!response.ok) {
            if (response.status === 401) {
              redirectToLogin();
            }
            throw new Error(response.status);
          }
          return response.json();
        }).then((result) => {
          if (result.tokenStatus === 'VALID') {
            const logData = {
              eventStatus: SPLUNK_EVENT_STATUS.SUCCESS,
              eventName: 'MYCA_LOGIN_REDIRECT_NOT_REQUIRED',
            };
            splunkLogQueue.push(logData);
            fetchUserLookup({
              connectApiUrl,
              redirectToLogin,
              splunkLogQueue,
              dispatch,
            });
          } else {
            redirectToLogin();
          }
        }).catch((err) => {
          console.log('User session api failed', err);
        });
      } else {
        // log the redirect to global login was needed
        const logData = {
          eventStatus: SPLUNK_EVENT_STATUS.SUCCESS,
          eventName: 'MYCA_LOGIN_REDIRECT_REQUIRED',
        };
        splunkLogQueue.push(logData);
        fetchUserLookup({
          connectApiUrl,
          redirectToLogin,
          splunkLogQueue,
          dispatch,
        });
      }
    } else if (window && window.opener) {
      dispatch(startInstantMembership(params.partner.toUpperCase()));
    } else {
      dispatch(initiateConnectPartner(params.partner.toUpperCase()));
    }
  }, []);

  // eslint-disable-next-line complexity -- need to check all conditions
  useEffect(() => {
    if (flow === 'logout') {
      let destPage = `${partnerConfig.url}/${partnerConfig.cancelCardPath}`;
      if (partner === 'AMAZON') {
        destPage = window.sessionStorage.getItem('partnerCancelUrl');
      }
      window.location.replace(`${destPage}`);
    }

    if (!prevIsInstantRef.current && isInstant && partner) {
      const path = `/${partner.toLowerCase()}/select`;
      router.push(path);
    }

    if (partner === 'BPAY' && currentFlow === UI_FLOWS.ERROR && error === STATUS_CODES.SYSTEM) {
      const path = '/bpay/message?provisionId=errorProvisionId&locale=en-AU';
      dispatch(mergeValues(fromJS({
        provisionId: 'errorProvisionId',
      })));
      router.push(path);
    }

    if ((prevCurrentFlowRef.current === UI_FLOWS.SELECT && currentFlow === UI_FLOWS.ERROR)
        || (!prevIsLoadedFromStorageRef.current && isLoadedFromStorage)
    ) {
      const path = getUrlStringFromQueryParams(`/${partner.toLowerCase()}/message`, {
        code,
        urlId,
        correlationId: urlCorrelationId,
        provisionId: urlProvisionId,
        locale: urlLocale,
        partnerFailureUri: urlPartnerFailureUri,
        mockCards,
        mockFlow,
        mockError,
      });
      router.push(path);
    }

    if (partner === 'BPAY' && prevProvisionIdRef.current === undefined
        && provisionId && urlLocale === 'en-AU' && currentFlow !== UI_FLOWS.ERROR) {
      const path = getUrlStringFromQueryParams(`/${partner.toLowerCase()}/select`, {
        code,
        provisionId: urlProvisionId,
        locale: urlLocale,
        partnerFailureUri: urlPartnerFailureUri,
        mockCards,
        mockFlow,
        mockError,
      });
      router.push(path);
    }

    if (partner !== 'BPAY' && prevCorrelationIdRef.current === undefined
        && correlationId
        && currentFlow !== UI_FLOWS.ERROR
        && source !== 'ICN'
        && !isLoadedFromStorage
    ) {
      const path = getUrlStringFromQueryParams(`/${partner.toLowerCase()}/select`, {
        code,
        urlId,
        correlationId,
        locale: urlLocale,
        partnerFailureUri,
        mockCards,
        mockFlow,
        mockError,
      });
      if (code === 'mockCode'
          || urlId === 'mockUrlId'
          || urlCorrelationId === correlationId) {
        router.push(path);
      } else if (isAuthenticated) {
        router.push(path);
      } else {
        const redirect = `${tenantUrl}${path}`;
        const loginUrl = `${amexUrl}/${urlLocale?.toLowerCase() || DEFAULT_LOCALE.toLowerCase()}/account/login?DestPage=${encodeURIComponent(redirect)}&ltype=connect`;
        window.location.replace(loginUrl);
      }
    }

    if (!prevHasUserCancelledRef.current && hasUserCancelled) {
      window.location.replace(`${amexUrl}/en-us/account/logout?target=${tenantUrl}/${partner}?flow=logout`);
    }

    if (prevLocaleRef.current !== locale) {
      dispatch(
        loadLanguagePack(MODULE_NAME, { fallbackLocale: DEFAULT_LOCALE })
      );
    }
    prevIsInstantRef.current = isInstant;
    prevProvisionIdRef.current = provisionId;
    prevCorrelationIdRef.current = correlationId;
    prevHasUserCancelledRef.current = hasUserCancelled;
    prevCurrentFlowRef.current = currentFlow;
    prevLocaleRef.current = locale;
    prevIsLoadedFromStorageRef.current = isLoadedFromStorage;
  },
  [
    partner,
    provisionId,
    correlationId,
    hasUserCancelled,
    currentFlow,
    error,
    locale,
    isLoadedFromStorage,
  ]);

  // Prepare URLs and memoized link renderers before any early return
  const privacyUrl = languageData?.links?.privacy || 'https://www.americanexpress.com/privacy';
  const termsUrl = languageData?.links?.terms || 'https://www.americanexpress.com/us/services/amex-card-on-file/terms-and-conditions/index.html';
  const renderPrivacyLink = useCallback(
    (chunks) => (
      <a href={privacyUrl} target="_blank" rel="noreferrer">{chunks}</a>
    ),
    [privacyUrl]
  );
  const renderTermsLink = useCallback(
    (chunks) => (
      <a href={termsUrl} target="_blank" rel="noreferrer">{chunks}</a>
    ),
    [termsUrl]
  );

  // render logic
  const { showLogoAnimation, showChildren } = getMainLayoutFields({
    partner,
    partnerConfig,
    isInstant,
    code,
    isAccountsLoaded,
    urlId,
    provisionId,
  });

  if (!partner) return null;

  return (
    <IntlProvider locale={locale} messages={languageData}>
      <div className={partner === 'PAYPAL' ? styles['paypal-page-wrapper'] : undefined}>
        <MainLayout
          isLoading={isLoading && !(isBreakpointUp('lg') && UI_FLOWS.isYellowFlow(currentFlow))}
          showLogoAnimation={showLogoAnimation}
          amexLogoSource={`${getBaseDlsResourcePath(DLS_VERSION)}/img/logos/dls-logo-bluebox-alt.svg`}
          amexLogoAltText="American express logo"
          partnerLogoSource={`${amexConnectBaseResourcePath}/images/${partnerConfig.logo}`}
          partnerLogoAltText={`${partnerConfig.text} logo`}
          footerMessage={(
            <FormattedMessage
              id="footer.message"
              defaultMessage="All users of our online services are subject to the <ps>Privacy Statement</ps> and agree to be bound by <tos>Terms of Service</tos>. © {year} American Express Company. All rights reserved."
              values={{
                ps: renderPrivacyLink,
                tos: renderTermsLink,
                year: new Date().getFullYear(),
              }}
            />
          )}
          partner={partner}
        >
          {showChildren ? children
            : (
              <div className="margin-4-t text-align-center">
                <GetErrorMessage
                  urlIParam={urlId}
                  provisionIdParam={urlProvisionId}
                  isMissingFields={isMissingFields}
                  partnerConfig={partnerConfig}
                  flow={flow}
                />
              </div>
            )}
        </MainLayout>
      </div>
    </IntlProvider>
  );
};

export const loadModuleData = ({ store: { dispatch } }) => dispatch(
  loadLanguagePack(MODULE_NAME, { fallbackLocale: DEFAULT_LOCALE })
);

AmexConnectPageWrapper.holocron = {
  name: MODULE_NAME,
  loadModuleData,
};
export const TestableAmexConnectPageWrapper = AmexConnectPageWrapper;

AmexConnectPageWrapper.propTypes = {
  children: PropTypes.node,
  router: PropTypes.shape({
    push: PropTypes.func,
  }),
  params: PropTypes.shape({
    partner: PropTypes.string,
  }),
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
  partnerConfig: PropTypes.shape({
    text: PropTypes.string,
    logo: PropTypes.string,
    url: PropTypes.string,
    cancelCardPath: PropTypes.string,
    isPull: PropTypes.bool,
  }),
  locale: PropTypes.string,
  languageData: PropTypes.shape({
    links: PropTypes.shape({
      privacy: PropTypes.string,
      terms: PropTypes.string,
    }),
  }),
};

const hocChain = compose(
  oneAppModuleWrapper(MODULE_NAME)
);

export default hocChain(AmexConnectPageWrapper);
